import React, { useState } from 'react';
import styled from 'styled-components';
import adminRanksService, { UserRank, CreateUserRankRequest, UpdateUserRankRequest } from '../../services/adminRanksService';
import RankFormModal from './RankFormModal';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { useNotification } from '../../hooks/useNotification';
import NotificationContainer from '../common/NotificationContainer';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 20px;
  color: #00d4ff;
  margin: 0;
`;

const AddButton = styled.button`
  background: #00d4ff;
  color: #000;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background: #00b8e6;
    transform: translateY(-1px);
  }
`;

const RanksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const RankCard = styled.div`
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #00d4ff;
    transform: translateY(-2px);
  }
`;

const RankHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const RankInfo = styled.div`
  flex: 1;
`;

const RankLevel = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #00d4ff;
  margin-bottom: 5px;
`;

const RankName = styled.div`
  font-size: 16px;
  color: #fff;
  margin-bottom: 5px;
`;

const FactionBadge = styled.div<{ $faction: string }>`
  background: ${props => props.$faction === 'human' ? '#3b82f6' : '#ff8800'};
  color: ${props => props.$faction === 'human' ? '#000' : '#fff'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-block;
`;

const RankIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IconImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const IconPlaceholder = styled.div`
  color: #666;
  font-size: 12px;
  text-align: center;
`;

const RankDetails = styled.div`
  margin-top: 15px;
`;

const PointsRequired = styled.div`
  color: #ccc;
  font-size: 14px;
  margin-bottom: 15px;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button<{ $variant?: 'edit' | 'delete' }>`
  background: ${props => 
    props.$variant === 'delete' ? '#f44336' : 
    props.$variant === 'edit' ? '#ff9800' : '#3b82f6'
  };
  color: ${props => props.$variant === 'edit' ? '#000' : '#fff'};
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.3s ease;
  flex: 1;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #00d4ff;
  font-size: 16px;
  padding: 40px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 16px;
  padding: 40px;
`;

const FactionTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const FactionTab = styled.button<{ $active: boolean; $faction: 'human' | 'alien' }>`
  background: ${props => props.$active 
    ? (props.$faction === 'human' ? '#3b82f6' : '#ff8800') 
    : 'transparent'};
  color: ${props => props.$active 
    ? (props.$faction === 'human' ? '#000' : '#fff') 
    : (props.$faction === 'human' ? '#3b82f6' : '#ff8800')};
  border: 2px solid ${props => props.$faction === 'human' ? '#3b82f6' : '#ff8800'};
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  font-size: 14px;

  &:hover {
    background: ${props => props.$faction === 'human' ? '#3b82f6' : '#ff8800'};
    color: ${props => props.$faction === 'human' ? '#000' : '#fff'};
  }
`;

interface UserRanksTabProps {
  ranks: UserRank[];
  loading: boolean;
  onRefresh: () => void;
}

const UserRanksTab: React.FC<UserRanksTabProps> = ({ ranks, loading, onRefresh }) => {
  const { confirm, modalProps } = useConfirmModal();
  const { notifications, removeNotification, showError } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRank, setEditingRank] = useState<UserRank | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [selectedFaction, setSelectedFaction] = useState<'human' | 'alien'>('human');

  const handleCreate = () => {
    setEditingRank(null);
    setIsModalOpen(true);
  };

  const handleEdit = (rank: UserRank) => {
    setEditingRank(rank);
    setIsModalOpen(true);
  };

  // Filter ranks by selected faction
  const filteredRanks = ranks.filter(rank => rank.faction === selectedFaction);

  const handleDelete = async (rank: UserRank) => {
    const confirmed = await confirm({
      title: 'Delete Rank',
      message: `Are you sure you want to delete rank "${rank.name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      setProcessing(rank.id);
      await adminRanksService.deleteUserRank(rank.id);
      onRefresh();
    } catch (error) {
      console.error('Error deleting rank:', error);
      showError('Error', 'Error deleting rank');
    } finally {
      setProcessing(null);
    }
  };

  const handleSave = async (data: any, imageFile?: File) => {
    try {
      if (editingRank) {
        await adminRanksService.updateUserRank(editingRank.id, data as UpdateUserRankRequest, imageFile);
      } else {
        await adminRanksService.createUserRank(data as CreateUserRankRequest, imageFile);
      }
      setIsModalOpen(false);
      onRefresh();
    } catch (error: any) {
      console.error('Error saving rank:', error);
      throw error;
    }
  };


  if (loading) {
    return <LoadingMessage>Loading user ranks...</LoadingMessage>;
  }

  return (
    <>
      <Header>
        <Title>User Ranks ({ranks.length})</Title>
        <AddButton onClick={handleCreate}>
          Add User Rank
        </AddButton>
      </Header>

      <FactionTabs>
        <FactionTab 
          $active={selectedFaction === 'human'} 
          $faction="human"
          onClick={() => setSelectedFaction('human')}
        >
          Human Forces ({ranks.filter(r => r.faction === 'human').length})
        </FactionTab>
        <FactionTab 
          $active={selectedFaction === 'alien'} 
          $faction="alien"
          onClick={() => setSelectedFaction('alien')}
        >
          Alien Alliance ({ranks.filter(r => r.faction === 'alien').length})
        </FactionTab>
      </FactionTabs>

      {filteredRanks.length === 0 ? (
        <EmptyMessage>No {selectedFaction} ranks found</EmptyMessage>
      ) : (
        <RanksGrid>
          {filteredRanks
            .sort((a, b) => a.level - b.level)
            .map((rank) => (
            <RankCard key={rank.id}>
              <RankHeader>
                <RankInfo>
                  <RankLevel>Level {rank.level}</RankLevel>
                  <RankName>{rank.name}</RankName>
                  <FactionBadge $faction={rank.faction}>
                    {rank.faction}
                  </FactionBadge>
                </RankInfo>
                <RankIcon>
                  {rank.iconUrl ? (
                    <IconImage src={rank.iconUrl} alt={rank.name} />
                  ) : (
                    <IconPlaceholder>No Icon</IconPlaceholder>
                  )}
                </RankIcon>
              </RankHeader>
              
              <RankDetails>
                <PointsRequired>
                  Points Required: {rank.pointsRequired}
                </PointsRequired>
                
                <ActionsContainer>
                  <ActionButton 
                    $variant="edit" 
                    onClick={() => handleEdit(rank)}
                    disabled={processing === rank.id}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton 
                    $variant="delete" 
                    onClick={() => handleDelete(rank)}
                    disabled={processing === rank.id}
                  >
                    {processing === rank.id ? 'Deleting...' : 'Delete'}
                  </ActionButton>
                </ActionsContainer>
              </RankDetails>
            </RankCard>
          ))}
        </RanksGrid>
      )}

      {isModalOpen && (
        <RankFormModal
          rank={editingRank}
          rankType="user"
          onSave={handleSave}
          onClose={() => setIsModalOpen(false)}
          initialFaction={editingRank ? undefined : selectedFaction}
        />
      )}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      <ConfirmModal {...modalProps} />
    </>
  );
};

export default UserRanksTab;